import { useState, FC, useId, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { NumberInput } from '@/shared/ui/NumberInput';
import { ChevronDown, AlertTriangle } from 'lucide-react';
import { RoomGeometry, Opening, Partition, WallElement } from '@/shared/model';
import { PartitionsManager } from './room-components/PartitionsManager';
import { OpeningsManager } from './room-components/OpeningsManager';
import { WallElementsManager } from './room-components/WallElementsManager';
import { ManualOverrides } from './room-components/ManualOverrides';
import { RoomStats } from './room-components/RoomStats';
import { useDebouncedCallback } from '@/shared/lib/hooks/use-debounced-callback';
import { useStorage } from '@/shared/lib/storage';
import { parseSafeNumber } from '@/shared/lib/number';

interface RoomInputProps {
  length: number;
  width: number;
  height: number;
  onChange: (dims: { 
    length?: number; 
    width?: number; 
    height?: number;
    manualFloorArea?: number;
    manualWallArea?: number;
    manualPerimeter?: number;
  }) => void;
  geometry: RoomGeometry;
  openings?: Opening[];
  onOpeningsChange?: (openings: Opening[]) => void;
  partitions?: Partition[];
  onPartitionsChange?: (partitions: Partition[]) => void;
  wallElements?: WallElement[];
  onWallElementsChange?: (elements: WallElement[]) => void;
}

const toCm = (m: number): number => {
  if (!m) return 0;
  return Math.round(m * 100 * 100) / 100;
};

// Пороги «здравого смысла» для габаритов комнаты (в метрах). НЕ блокируют ввод —
// промышленные объекты бывают крупнее, — а лишь предупреждают о вероятной ошибке
// единиц (например, значение введено не в тех единицах и площадь раздувается на порядки).
const MAX_REASONABLE_ROOM_SIDE_M = 50; // длина/ширина: жильё/типовая отделка редко > 50 м
const MAX_REASONABLE_CEILING_M = 6;    // высота потолка: даже промзал редко > 6 м

export const RoomInput: FC<RoomInputProps> = ({ 
  length, width, height, onChange, geometry, 
  openings = [], onOpeningsChange, 
  partitions = [], onPartitionsChange,
  wallElements = [], onWallElementsChange
}) => {
  const { t } = useTranslation();
  const lengthId = useId();
  const widthId = useId();
  const heightId = useId();
  
  const [localDims, setLocalDims] = useState({
    length: toCm(length),
    width: toCm(width),
    height: toCm(height)
  });

  const [activeField, setActiveField] = useState<string | null>(null);

  const [prevProps, setPrevProps] = useState({ length, width, height });
  if (prevProps.length !== length || prevProps.width !== width || prevProps.height !== height) {
      setPrevProps({ length, width, height });
      const updatedDims = { ...localDims };
      let changed = false;
      const propCmLength = toCm(length);
      if (activeField !== 'length' && updatedDims.length !== propCmLength) {
          updatedDims.length = propCmLength;
          changed = true;
      }
      const propCmWidth = toCm(width);
      if (activeField !== 'width' && updatedDims.width !== propCmWidth) {
          updatedDims.width = propCmWidth;
          changed = true;
      }
      const propCmHeight = toCm(height);
      if (activeField !== 'height' && updatedDims.height !== propCmHeight) {
          updatedDims.height = propCmHeight;
          changed = true;
      }
      if (changed) setLocalDims(updatedDims);
  }

  // Persisted via shared storage (idb preferred, falls back to localStorage with validation/migrate)
  const [isCollapsed, setIsCollapsed] = useStorage<boolean>('bg2_roomInput_collapsed', false);

  const debouncedOnChange = useDebouncedCallback(onChange, 150);

  const handleChange = (field: 'length' | 'width' | 'height', value: number) => {
    setLocalDims(prev => ({ ...prev, [field]: value }));
    const meters = parseSafeNumber(value) / 100;
    debouncedOnChange({ [field]: meters });
  };

  const handleBlur = (field: 'length' | 'width' | 'height') => {
    setActiveField(null);
    const cm = localDims[field];
    const meters = parseSafeNumber(cm) / 100;
    onChange({ [field]: meters });
    const formatted = meters > 0 ? Math.round(meters * 100 * 100) / 100 : 0;
    setLocalDims(prev => ({ ...prev, [field]: formatted }));
  };

  const handleFocus = (field: string) => setActiveField(field);

  const geometryPanelId = useId();
  const handleHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsCollapsed(!isCollapsed);
    }
  };

  const hasOversizedDims =
    length > MAX_REASONABLE_ROOM_SIDE_M ||
    width > MAX_REASONABLE_ROOM_SIDE_M ||
    height > MAX_REASONABLE_CEILING_M;

  return (
    <div className="transition-all duration-300">
      <div
        className="flex justify-between items-center cursor-pointer select-none pb-4 border-b border-slate-100 dark:border-slate-800 group rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
        onClick={() => setIsCollapsed(!isCollapsed)}
        role="button"
        tabIndex={0}
        onKeyDown={handleHeaderKeyDown}
        aria-expanded={!isCollapsed}
        aria-controls={geometryPanelId}
        aria-label={t('room_input.title')}
      >
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors flex items-center gap-1.5">
                <span className="text-xl">📏</span>
                <span>{t('room_input.title')}</span>
            </h2>
            <AnimatePresence>
                {isCollapsed && (
                    /* eslint-disable i18next/no-literal-string */
                    <motion.span
                        initial={{ opacity: 0, scale: 0.92, x: -6 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.92, x: -6 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-extrabold px-2.5 py-0.5 rounded-full ml-3 border border-slate-200/50 dark:border-slate-700/50 shadow-sm"
                    >
                        {length ? Math.round(length * 100) : 0} × {width ? Math.round(width * 100) : 0} {t('room_input.cm_suffix', 'см')} • H: {height ? Math.round(height * 100) : 0}
                    </motion.span>
                    /* eslint-enable i18next/no-literal-string */
                )}
            </AnimatePresence>
          </div>
          <motion.div
              animate={{ rotate: isCollapsed ? 0 : 180 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="p-1 text-slate-400 dark:text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
              aria-hidden="true"
          >
              <ChevronDown size={22}/>
          </motion.div>
      </div>

      <AnimatePresence initial={false}>
        {!isCollapsed && (
            <motion.div
                id={geometryPanelId}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                    height: { type: 'spring', stiffness: 200, damping: 25 },
                    opacity: { duration: 0.2 }
                }}
                style={{ willChange: 'height, opacity' }}
                className="overflow-hidden"
            >
                <div className="pt-5 pb-1">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <label htmlFor={lengthId} className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block cursor-pointer">
                                {t('room_input.length_cm', 'Длина (см)')}
                            </label>
                            <NumberInput 
                                id={lengthId}
                                value={localDims.length} onChange={(val) => handleChange('length', val)}
                                onFocus={() => handleFocus('length')} onBlur={() => handleBlur('length')} placeholder="0" 
                            />
                        </div>
                        <div>
                            <label htmlFor={widthId} className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block cursor-pointer">
                                {t('room_input.width_cm', 'Ширина (см)')}
                            </label>
                            <NumberInput 
                                id={widthId}
                                value={localDims.width} onChange={(val) => handleChange('width', val)}
                                onFocus={() => handleFocus('width')} onBlur={() => handleBlur('width')} placeholder="0" 
                            />
                        </div>
                        <div>
                            <label htmlFor={heightId} className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block cursor-pointer">
                                {t('room_input.height_cm', 'Высота потолка (см)')}
                            </label>
                            <NumberInput 
                                id={heightId}
                                value={localDims.height} onChange={(val) => handleChange('height', val)}
                                onFocus={() => handleFocus('height')} onBlur={() => handleBlur('height')} placeholder="0" 
                            />
                        </div>
                    </div>

                    <AnimatePresence>
                        {hasOversizedDims && (
                            <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                role="alert"
                                className="flex items-start gap-2 -mt-3 mb-6 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 text-xs font-medium"
                            >
                                <AlertTriangle size={15} className="shrink-0 mt-0.5" aria-hidden="true" />
                                <span>{t('room_input.size_warning', 'Необычно большие габариты. Проверьте, что значения введены в сантиметрах.')}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <ManualOverrides geometry={geometry} onChange={onChange} />

                    {onPartitionsChange && <PartitionsManager partitions={partitions} onChange={onPartitionsChange} roomHeight={height} />}
                    {onOpeningsChange && <OpeningsManager openings={openings} onChange={onOpeningsChange} maxWallWidth={Math.min(length, width)} maxWallHeight={height} />}
                    {onWallElementsChange && <WallElementsManager elements={wallElements} onChange={onWallElementsChange} roomHeight={height} />}

                    <RoomStats geometry={geometry} />
                </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RoomInput;